import { inject, Pipe, PipeTransform, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';

/** Opening or closing line of a fenced code block, plus its info string. */
const FENCE_LINE = /^ {0,3}(`{3,}|~{3,})(.*)$/;

/** A line that holds nothing but one bold run, e.g. `**Titel en doel**`. */
const BOLD_TITLE_LINE = /^ {0,3}\*\*([^*]+)\*\*:?[ \t]*$/;

/** A bulleted or numbered list item. */
const LIST_ITEM_LINE = /^[ \t]*(?:[-*+]|\d+[.)])(?:[ \t]|$)/;

@Pipe({
  name: 'markdown',
})
export class MarkdownPipe implements PipeTransform {
  private readonly _sanitizer = inject(DomSanitizer);

  transform(value: string | null | undefined): SafeHtml | string {
    if (!value) {
      return '';
    }

    try {
      const preprocessed = this._preprocessMarkdown(value);

      const rawHtml = marked.parse(preprocessed, {
        breaks: true,
        gfm: true,
      }) as string;

      const sanitizedHtml = this._sanitizer.sanitize(
        SecurityContext.HTML,
        rawHtml
      );

      return this._sanitizer.bypassSecurityTrustHtml(sanitizedHtml || '');
    } catch {
      return value;
    }
  }

  /**
   * Pre-processes raw markdown text to ensure headings, lists, and sections
   * have adequate spacing and semantic tags even when formatted with
   * compressed newlines.
   *
   * Walks the text line by line rather than running global regexes over it: an
   * agent message can carry a fenced code block -- the raw proposal, whenever
   * it failed to parse into a preview card -- and rewriting the lines inside
   * that fence would corrupt the very text it exists to show verbatim.
   */
  private _preprocessMarkdown(text: string): string {
    const processed: string[] = [];
    let openFence = '';
    let inListItem = false;

    for (const line of text.split('\n')) {
      const fence = FENCE_LINE.exec(line);

      if (openFence) {
        // A fence closes on the same character, at least as long, and bare.
        if (
          fence &&
          fence[1][0] === openFence[0] &&
          fence[1].length >= openFence.length &&
          !fence[2].trim()
        ) {
          openFence = '';
        }
        processed.push(line);
        continue;
      }

      if (fence) {
        openFence = fence[1];
        processed.push(line);
        continue;
      }

      // 1. Elevate a standalone bold title -- `**Titel en doel**`, `**Vervolg:**`
      // -- to a `### Titel` heading so it gets dedicated heading typography and
      // clear section spacing. Inside a list item such a line is the item's own
      // text rather than a section title, so it is left alone there.
      const boldTitle = inListItem ? null : BOLD_TITLE_LINE.exec(line);
      if (boldTitle) {
        // The colon of `**Vervolg:**` or `**Vervolg**:` belongs to the label,
        // not to the heading it becomes.
        const title = boldTitle[1].trim().replace(/:$/, '');
        if (processed.at(-1)?.trim()) {
          processed.push('');
        }
        processed.push(`### ${title}`, '');
        continue;
      }

      // 2. Give a list that follows straight on from a line of normal text its
      // own empty line. Bullets and a `1.` interrupt a paragraph by themselves,
      // but a list numbered from anything else does not and would otherwise
      // render as part of the sentence above it.
      const isListItem = LIST_ITEM_LINE.test(line);
      if (isListItem && !inListItem && processed.at(-1)?.trim()) {
        processed.push('');
      }

      // A list item opens a list; a blank line is the first thing that can end
      // one. Anything else in between continues the item it follows.
      if (isListItem) {
        inListItem = true;
      } else if (!line.trim()) {
        inListItem = false;
      }

      processed.push(line);
    }

    return processed.join('\n');
  }
}
